import React, { useRef, useState,useEffect  } from "react";
import axios from "axios";
import Footer from "../components/Footer.jsx";
import { motion } from "framer-motion";
import logo from "../assets/images/logo-login.png";
import loginCar from "../assets/images/login-car.png";
import "../styles/LoginPage.css";
import { API_BASE_URL } from "../utils/api.js";
import MobileFooterSheet from "../components/MobileFooterSheet.jsx";

function useWebOTP(setCode) {
  useEffect(() => {
    let abortController = new AbortController();

    async function run() {
      if (!("OTPCredential" in window) || !navigator.credentials) return;

      try {
        // Must be triggered after a user gesture in most browsers. If needed,
        // call run() after the user focuses an OTP field or presses a button.
        const otp = await navigator.credentials.get({
          otp: { transport: ["sms"] },
          signal: abortController.signal,
        });

        // otp.code is the full code string, e.g., "123456"
        if (otp && otp.code) {
          const digits = otp.code.replace(/\D/g, "").slice(0, 4).split("");
          if (digits.length) {
            // setCode expects an array of 6 strings (as in your component)
            setCode((prev) => {
              const next = Array(4).fill("");
              digits.forEach((d, i) => (next[i] = d));
              return next;
            });
          }
        }
      } catch (err) {
        // AbortError is normal if the user navigates or timeout occurs
        // console.debug("WebOTP aborted/failed", err);
      }
    }

    run();

    return () => {
      abortController.abort();
    };
  }, [setCode]);
}

export default function CodeVerify({ onBack, setCurrentView, onVerified }) {
  // pull saved phone/user from localStorage (set earlier during register)
  const savedPhone = localStorage.getItem("pending_verification_phone") || "";
  const userId = localStorage.getItem("pending_verification_user_id") || "";

  const [code, setCode] = useState(Array(4).fill(""));
  useWebOTP(setCode);
  const inputsRef = useRef([]);
  const [submitting, setSubmitting] = useState(false);

  const maskPhone = (p) => {
    if (!p) return "*********5";
    const last = p.replace(/\s/g, "").slice(-1);
    return last +"********" + "966+";
  };

  const focusNext = (idx) => {
    if (idx < inputsRef.current.length - 1) inputsRef.current[idx + 1]?.focus();
  };
  const focusPrev = (idx) => {
    if (idx > 0) inputsRef.current[idx - 1]?.focus();
  };

  const handleChange = (idx, val) => {
    const v = (val || "").replace(/\D/g, "").slice(-1);
    const next = [...code];
    next[idx] = v;
    setCode(next);
    if (v) focusNext(idx);
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !code[idx]) {
      e.preventDefault();
      focusPrev(idx);
    }
    if (e.key === "ArrowLeft") { e.preventDefault(); focusPrev(idx); }
    if (e.key === "ArrowRight") { e.preventDefault(); focusNext(idx); }
  };

  const handlePaste = (e) => {
    const pasted = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 4);
    if (!pasted) return;
    e.preventDefault();
    const next = [...code];
    pasted.split("").forEach((ch, i) => (next[i] = ch));
    setCode(next);
    const lastIndex = Math.min(pasted.length, 4) - 1;
    inputsRef.current[lastIndex]?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const entered = code.join("");
    if (entered.length !== 4) return;

    setSubmitting(true);
    try {
      // Adjust payload to match your backend (user_id vs phone)
      // If your backend verifies with user_id:
      // const { data } = await axios.post(`${API_BASE_URL}/verify-otp`, { user_id: userId, code: entered });

      // If your backend verifies with phone:
      const { data } = await axios.post(`${API_BASE_URL}/verify-otp/reset-password`, { phone: savedPhone, code: entered });

      localStorage.setItem("token", data.reset_token);
      // Placeholder for demo
      await new Promise((r) => setTimeout(r, 600));

      // on success: clear temp storage and move on
      localStorage.removeItem("pending_verification_user_id");
      localStorage.removeItem("pending_verification_phone");

      if (onVerified) onVerified();                 // optional callback
      else if (setCurrentView) setCurrentView("reset"); // your existing view switch
      // else fallback: window.location.assign("/");
    } catch (err) {
      console.error(err);
      alert("فشل التحقق من الرمز. تأكد من الرمز وحاول مجدداً.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div dir="rtl" className="page">
      {/* Back */}
      <motion.button
        className="back-pill"
        onClick={onBack}
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.98 }}
        type="button"
      >
        <span>ارجع للخلف</span>
      </motion.button>

      <div className="shell">
        {/* Right: hero */}
        <section className="heroPane">
          <div className="brand">
            <img src={logo} alt="PARTECH" className="brand-logo" />
          </div>
          <div className="carWrap" aria-hidden>
            <img src={loginCar} alt="Sports car" />
          </div>
        </section>

        {/* Left: code form */}
        <section className="formPane">
          <header className="formHeader">
            <h2 className="otp-title">تم إرسال رمز التحقق إلى الرقم {maskPhone(savedPhone)}</h2>
            <p className="otp-sub">أدخل رمز التحقق المرسل إلى جوالك</p>
          </header>

          <form onSubmit={handleSubmit} className="form" dir="ltr">
            <div className="otp-boxes" onPaste={handlePaste}>
              {code.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => (inputsRef.current[i] = el)}
                  className="otp-input"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="one-time-code"
                  maxLength={1}
                  value={d}
                  onChange={(e) => handleChange(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  aria-label={`الرقم ${i + 1}`}
                />
              ))}
            </div>

            <button
              className="cta"
              type="submit"
              disabled={submitting || code.join("").length !== 4}
            >
              {submitting ? "..." : "تأكيد"}
            </button>
          </form>
        </section>
      </div>

      {/* Desktop footer stays; mobile footer is a bottom-sheet */}
      <div className="desktop-only">
        <Footer setCurrentView={setCurrentView} />
      </div>

        <MobileFooterSheet mode="sticky" />
      
    </div>
  );
}
